import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, ScrollView, Alert, Modal, TextInput as RNTextInput } from 'react-native';
import { Text, Card, ActivityIndicator, Button, Divider, Chip, RadioButton } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import * as FileSystem from 'expo-file-system/legacy';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { generateFacturaHTML, generateTicketHTML, resolveItemPresentacion } from '../../utils/facturaGenerator';

const ESTADOS_CONFIG = {
  pendiente:    { label: 'Pendiente',    color: '#FFC107', icon: 'time',              textColor: '#000' },
  preparado:    { label: 'Preparado',    color: '#17A2B8', icon: 'checkmark-done',    textColor: '#fff' },
  en_ruta:      { label: 'En Ruta',      color: '#6F42C1', icon: 'car',               textColor: '#fff' },
  entregado:    { label: 'Entregado',    color: '#28A745', icon: 'checkmark-circle',  textColor: '#fff' },
  completada:   { label: 'Completada',   color: '#28A745', icon: 'checkmark-circle',  textColor: '#fff' },
  cancelada:    { label: 'Cancelada',    color: '#DC3545', icon: 'close-circle',      textColor: '#fff' },
  devuelto:     { label: 'Devuelto',     color: '#FD7E14', icon: 'return-down-back',  textColor: '#fff' },
  parcialmente_devuelto: { label: 'Parc. Devuelto', color: '#FD7E14', icon: 'return-down-back', textColor: '#fff' },
};

const TRANSICIONES = {
  pendiente:  [{ estado: 'preparado', label: 'Confirmar Bodega', icon: 'checkmark-done', color: '#17A2B8', roles: ['bodega', 'admin'] }],
  preparado:  [{ estado: 'en_ruta', label: 'Iniciar Entrega', icon: 'car', color: '#6F42C1', roles: ['repartidor', 'vendedor', 'admin'] }],
  en_ruta:    [{ estado: 'entregado', label: 'Marcar Entregado', icon: 'checkmark-circle', color: '#28A745', roles: ['repartidor', 'vendedor', 'admin'] }],
  entregado:  [{ estado: 'completada', label: 'Completar', icon: 'checkmark-done-circle', color: '#28A745', roles: ['repartidor', 'vendedor', 'admin'] }],
};

const FLUJO_ESTADOS = ['pendiente', 'preparado', 'en_ruta', 'entregado', 'completada'];

const DetalleVentaScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { user } = useAuth();
  const { venta: ventaParam } = route.params;

  const [loading, setLoading] = useState(true);
  const [venta, setVenta] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [configuracionNegocio, setConfiguracionNegocio] = useState(null);
  const [changingEstado, setChangingEstado] = useState(false);
  const [showEntregaModal, setShowEntregaModal] = useState(false);
  const [metodoPagoEntrega, setMetodoPagoEntrega] = useState('efectivo');
  const [notasTransicion, setNotasTransicion] = useState('');

  const getEstadoConfig = (estado) => ESTADOS_CONFIG[estado] || ESTADOS_CONFIG.pendiente;

  const loadVentaDetalle = useCallback(async () => {
    try {
      setLoading(true);
      const [detallesVenta, historialData] = await Promise.all([
        api.getVenta(ventaParam.id),
        api.getHistorialPedido(ventaParam.id)
      ]);

      if (detallesVenta && detallesVenta.items) {
        setVenta({
          ...detallesVenta,
          detalles: detallesVenta.items,
          usuario: { id: detallesVenta.usuario_id, nombre: detallesVenta.vendedor_nombre },
          cliente: {
            id: detallesVenta.cliente_id,
            nombre: detallesVenta.cliente_nombre,
            direccion: detallesVenta.cliente_direccion,
            telefono: detallesVenta.cliente_telefono
          }
        });
      } else {
        setVenta({ ...ventaParam, items: [], detalles: [] });
      }

      setHistorial(historialData || []);
    } catch (error) {
      console.error('Error al cargar detalles:', error);
      setVenta({ ...ventaParam, detalles: [], items: [] });
    } finally {
      setLoading(false);
    }
  }, [ventaParam]);

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <Ionicons
          name="arrow-back"
          size={24}
          color="#007bff"
          style={{ marginLeft: 8 }}
          onPress={() => navigation.goBack()}
        />
      ),
    });
    loadVentaDetalle();
    loadConfiguracion();
  }, [loadVentaDetalle]);

  const loadConfiguracion = async () => {
    try {
      const config = await api.getConfiguracion();

      // Si no hay logo en la DB, intentar usar el logo local
      if (!config.logo_url) {
        try {
          const logoPath = FileSystem.documentDirectory + 'logo.png';
          const logoExists = await FileSystem.getInfoAsync(logoPath);
          if (logoExists.exists) {
            const base64 = await FileSystem.readAsStringAsync(logoPath, {
              encoding: FileSystem.EncodingType.Base64,
            });
            config.logo_url = `data:image/png;base64,${base64}`;
          }
        } catch (logoError) {
          console.log('No se pudo cargar logo local:', logoError);
        }
      }

      setConfiguracionNegocio(config);
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    }
  };

  const handleCambiarEstado = async (nuevoEstado, extraData = {}) => {
    const estadoConfig = getEstadoConfig(nuevoEstado);

    Alert.alert(
      'Confirmar Cambio',
      `¿Cambiar estado a "${estadoConfig.label}"?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Confirmar',
          onPress: async () => {
            try {
              setChangingEstado(true);
              await api.cambiarEstadoPedido(venta.id, {
                nuevo_estado: nuevoEstado,
                notas: extraData.notas || null,
                metodo_pago: extraData.metodo_pago || null,
              });
              Alert.alert('Estado Actualizado', `Pedido #${venta.id} ahora está: ${estadoConfig.label}`);
              loadVentaDetalle();
            } catch (error) {
              Alert.alert('Error', error.message);
            } finally {
              setChangingEstado(false);
            }
          }
        }
      ]
    );
  };

  const handleEntrega = () => {
    setShowEntregaModal(true);
  };

  const confirmarEntrega = async () => {
    setShowEntregaModal(false);
    try {
      setChangingEstado(true);
      await api.cambiarEstadoPedido(venta.id, {
        nuevo_estado: 'entregado',
        notas: notasTransicion || 'Entregado sin novedad',
        metodo_pago: metodoPagoEntrega,
      });
      Alert.alert('Entregado', `Pedido #${venta.id} marcado como entregado`);
      setNotasTransicion('');
      loadVentaDetalle();
    } catch (error) {
      Alert.alert('Error', error.message);
    } finally {
      setChangingEstado(false);
    }
  };

  const handleCancelarVenta = () => {
    const yaProcesada = ['en_ruta', 'entregado', 'completada'].includes(venta?.estado);
    const mensaje = yaProcesada
      ? 'Esta venta ya estaba procesada. Cancelarla restaurará el stock al inventario y eliminará la cuenta por cobrar si la hubiera. ¿Continuar?'
      : '¿Está seguro de que desea cancelar este pedido?';
    Alert.alert(
      'Confirmar Cancelación',
      mensaje,
      [
        { text: 'No', style: 'cancel' },
        {
          text: 'Sí, Cancelar',
          style: 'destructive',
          onPress: async () => {
            try {
              setChangingEstado(true);
              await api.cambiarEstadoPedido(venta.id, {
                nuevo_estado: 'cancelada',
                notas: 'Cancelado por el usuario',
              });
              Alert.alert('Cancelado', 'El pedido ha sido cancelado');
              navigation.goBack();
            } catch (error) {
              Alert.alert('Error', error.message);
            } finally {
              setChangingEstado(false);
            }
          }
        }
      ]
    );
  };

  const generateAndSharePDF = async (format = 'factura') => {
    try {
      if (!venta) return;
      setGeneratingPDF(true);
      const generador = format === 'ticket' ? generateTicketHTML : generateFacturaHTML;
      const html = generador(venta, configuracionNegocio || {}, { showInternalId: false });
      const { uri } = await Print.printToFileAsync({ html, base64: false });
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri);
      } else {
        Alert.alert('Error', 'La función de compartir no está disponible');
      }
    } catch (error) {
      Alert.alert('Error', 'No se pudo generar el PDF: ' + error.message);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const renderProgressTimeline = () => {
    if (!venta) return null;
    const estadoActual = venta.estado;
    const isCancelado = estadoActual === 'cancelada';
    const isDevuelto = estadoActual === 'devuelto' || estadoActual === 'parcialmente_devuelto';
    const idxActual = FLUJO_ESTADOS.indexOf(estadoActual);

    return (
      <Card style={styles.card}>
        <Card.Title title="Progreso del Pedido" />
        <Card.Content>
          <View style={styles.timelineContainer}>
            {FLUJO_ESTADOS.map((e, i) => {
              const config = getEstadoConfig(e);
              const isActive = i <= idxActual && !isCancelado && !isDevuelto;
              const isCurrent = e === estadoActual;
              const isLast = i === FLUJO_ESTADOS.length - 1;

              return (
                <View key={e} style={styles.timelineRow}>
                  <View style={styles.timelineLeft}>
                    <View style={[
                      styles.timelineDot,
                      { backgroundColor: isActive ? config.color : (isCancelado ? '#DC3545' : '#DDD') },
                      isCurrent && styles.timelineDotCurrent
                    ]}>
                      <Ionicons
                        name={isActive ? config.icon : (isCancelado ? 'close' : 'ellipse')}
                        size={isCurrent ? 16 : 12}
                        color={isActive || isCancelado ? '#fff' : '#999'}
                      />
                    </View>
                    {!isLast && (
                      <View style={[
                        styles.timelineLine,
                        { backgroundColor: isActive && i < idxActual ? getEstadoConfig(FLUJO_ESTADOS[i + 1]).color : '#DDD' }
                      ]} />
                    )}
                  </View>
                  <View style={[styles.timelineContent, isCurrent && styles.timelineContentCurrent]}>
                    <Text style={[styles.timelineLabel, isActive && { fontWeight: 'bold', color: '#333' }]}>
                      {config.label}
                    </Text>
                    {historial.filter(h => h.estado_nuevo === e).map(h => (
                      <Text key={h.id} style={styles.timelineDetail}>
                        {new Date(h.creado_en).toLocaleString('es-HN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        {h.usuario_nombre ? ` - ${h.usuario_nombre}` : ''}
                        {h.notas ? `\n${h.notas}` : ''}
                      </Text>
                    ))}
                  </View>
                </View>
              );
            })}

            {isCancelado && (
              <View style={styles.timelineRow}>
                <View style={styles.timelineLeft}>
                  <View style={[styles.timelineDot, { backgroundColor: '#DC3545' }, styles.timelineDotCurrent]}>
                    <Ionicons name="close-circle" size={16} color="#fff" />
                  </View>
                </View>
                <View style={[styles.timelineContent, styles.timelineContentCurrent]}>
                  <Text style={[styles.timelineLabel, { fontWeight: 'bold', color: '#DC3545' }]}>Cancelado</Text>
                  {historial.filter(h => h.estado_nuevo === 'cancelada').map(h => (
                    <Text key={h.id} style={styles.timelineDetail}>
                      {new Date(h.creado_en).toLocaleString('es-HN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      {h.usuario_nombre ? ` - ${h.usuario_nombre}` : ''}
                      {h.notas ? `\n${h.notas}` : ''}
                    </Text>
                  ))}
                </View>
              </View>
            )}
          </View>
        </Card.Content>
      </Card>
    );
  };

  const renderAcciones = () => {
    if (!venta || !user) return null;
    const estado = venta.estado;
    const transiciones = TRANSICIONES[estado] || [];
    // Cancelable: estados activos por vendedor/admin (pendiente, preparado),
    // o estados ya entregados / completados solo por admin (restaura stock + CxC).
    const cancelableVendedor = ['pendiente', 'preparado'].includes(estado) && ['vendedor', 'admin'].includes(user.rol);
    const cancelableAdmin = ['en_ruta', 'entregado', 'completada'].includes(estado) && user.rol === 'admin';
    const canCancel = cancelableVendedor || cancelableAdmin;

    // Filtrar transiciones por rol del usuario
    const transicionesPermitidas = transiciones.filter(t => t.roles.includes(user.rol));

    if (transicionesPermitidas.length === 0 && !canCancel && estado !== 'en_ruta') return null;

    return (
      <Card style={styles.card}>
        <Card.Title title="Acciones" />
        <Card.Content>
          {transicionesPermitidas.map(t => {
            // Si es entrega, mostrar modal especial
            if (t.estado === 'entregado') {
              return (
                <Button
                  key={t.estado}
                  mode="contained"
                  icon={t.icon}
                  onPress={handleEntrega}
                  style={[styles.actionBtn, { backgroundColor: t.color }]}
                  loading={changingEstado}
                  disabled={changingEstado}
                >
                  {t.label}
                </Button>
              );
            }

            return (
              <Button
                key={t.estado}
                mode="contained"
                icon={t.icon}
                onPress={() => handleCambiarEstado(t.estado)}
                style={[styles.actionBtn, { backgroundColor: t.color }]}
                loading={changingEstado}
                disabled={changingEstado}
              >
                {t.label}
              </Button>
            );
          })}

          {canCancel && (
            <Button
              mode="outlined"
              icon="close-circle"
              onPress={handleCancelarVenta}
              style={styles.actionBtn}
              textColor="#DC3545"
              loading={changingEstado}
              disabled={changingEstado}
            >
              Cancelar Pedido
            </Button>
          )}
        </Card.Content>
      </Card>
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
        <Text style={styles.loadingText}>Cargando detalles...</Text>
      </View>
    );
  }

  if (!venta) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>No se pudo cargar la información</Text>
        <Button mode="contained" onPress={() => navigation.goBack()}>Volver</Button>
      </View>
    );
  }

  const estadoConfig = getEstadoConfig(venta.estado);

  return (
    <ScrollView style={styles.container}>
      {/* Info del pedido */}
      <Card style={styles.card}>
        <Card.Title
          title={`Pedido #${venta.id}`}
          right={() => (
            <Chip
              style={[styles.estadoChip, { backgroundColor: estadoConfig.color }]}
              textStyle={[styles.estadoChipText, { color: estadoConfig.textColor }]}
              icon={() => <Ionicons name={estadoConfig.icon} size={16} color={estadoConfig.textColor} />}
            >
              {estadoConfig.label.toUpperCase()}
            </Chip>
          )}
        />
        <Card.Content>
          <View style={styles.infoRow}>
            <View style={styles.infoIconContainer}>
              <Ionicons name="calendar" size={16} color="#666" />
              <Text style={styles.infoLabel}>Fecha:</Text>
            </View>
            <Text style={styles.infoValue}>{new Date(venta.fecha).toLocaleDateString('es-HN', {
              year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
            })}</Text>
          </View>

          <View style={styles.infoRow}>
            <View style={styles.infoIconContainer}>
              <Ionicons name="person" size={16} color="#666" />
              <Text style={styles.infoLabel}>Cliente:</Text>
            </View>
            <Text style={styles.infoValue}>{venta.cliente?.nombre || venta.cliente_nombre || 'No especificado'}</Text>
          </View>

          {(venta.cliente?.direccion || venta.cliente_direccion) && (
            <View style={styles.infoRow}>
              <View style={styles.infoIconContainer}>
                <Ionicons name="location" size={16} color="#666" />
                <Text style={styles.infoLabel}>Dirección:</Text>
              </View>
              <Text style={styles.infoValue}>{venta.cliente?.direccion || venta.cliente_direccion}</Text>
            </View>
          )}

          {(venta.cliente?.telefono || venta.cliente_telefono) && (
            <View style={styles.infoRow}>
              <View style={styles.infoIconContainer}>
                <Ionicons name="call" size={16} color="#666" />
                <Text style={styles.infoLabel}>Teléfono:</Text>
              </View>
              <Text style={styles.infoValue}>{venta.cliente?.telefono || venta.cliente_telefono}</Text>
            </View>
          )}

          <View style={styles.infoRow}>
            <View style={styles.infoIconContainer}>
              <Ionicons name="business" size={16} color="#666" />
              <Text style={styles.infoLabel}>Vendedor:</Text>
            </View>
            <Text style={styles.infoValue}>{venta.usuario?.nombre || venta.vendedor_nombre || 'No especificado'}</Text>
          </View>

          {venta.bodega_nombre && (
            <View style={styles.infoRow}>
              <View style={styles.infoIconContainer}>
                <Ionicons name="cube" size={16} color="#17A2B8" />
                <Text style={styles.infoLabel}>Bodega:</Text>
              </View>
              <Text style={styles.infoValue}>{venta.bodega_nombre}</Text>
            </View>
          )}

          {venta.repartidor_nombre && (
            <View style={styles.infoRow}>
              <View style={styles.infoIconContainer}>
                <Ionicons name="car" size={16} color="#6F42C1" />
                <Text style={styles.infoLabel}>Repartidor:</Text>
              </View>
              <Text style={styles.infoValue}>{venta.repartidor_nombre}</Text>
            </View>
          )}

          <View style={styles.infoRow}>
            <View style={styles.infoIconContainer}>
              <Ionicons name={venta.metodo_pago === 'efectivo' ? 'cash' : venta.metodo_pago === 'pendiente' ? 'time' : 'card'} size={16} color="#666" />
              <Text style={styles.infoLabel}>Pago:</Text>
            </View>
            <Text style={styles.infoValue}>{(venta.metodo_pago || 'pendiente').toUpperCase()}</Text>
          </View>

          {venta.notas && (
            <View style={styles.notasContainer}>
              <Ionicons name="document-text" size={16} color="#666" />
              <Text style={styles.notasText}> {venta.notas}</Text>
            </View>
          )}
        </Card.Content>
      </Card>

      {/* Timeline de progreso */}
      {renderProgressTimeline()}

      {/* Productos */}
      <Card style={styles.card}>
        <Card.Title title="Productos" />
        <Card.Content>
          {(venta.detalles || venta.items || []).map((item, index) => {
            const descMonto = parseFloat(item.descuento_monto || 0);
            const descValor = parseFloat(item.descuento_valor || 0);
            const { cantidadDisplay, precioDisplay, equivBase, precioBase, tienePresentacion, unidad } =
              resolveItemPresentacion(item);
            return (
              <View key={index}>
                <View style={styles.itemRow}>
                  <View style={styles.itemInfo}>
                    <Text style={styles.itemName}>{item.producto_nombre || 'Producto'}</Text>
                    <Text style={styles.itemPrice}>
                      L. {precioDisplay.toFixed(2)} x {Number.isInteger(cantidadDisplay) ? cantidadDisplay : cantidadDisplay.toFixed(2)} {unidad}
                    </Text>
                    {tienePresentacion ? (
                      <Text style={[styles.itemPrice, { fontSize: 11, color: '#888' }]}>
                        = {Number.isInteger(equivBase) ? equivBase : equivBase.toFixed(2)} unidades (L. {precioBase.toFixed(2)} c/u)
                      </Text>
                    ) : null}
                    {descMonto > 0 ? (
                      <Text style={styles.itemDescuento}>
                        Desc: −L. {descMonto.toFixed(2)}
                        {item.descuento_tipo === 'porcentaje' ? ` (${descValor}%)` : ''}
                      </Text>
                    ) : null}
                  </View>
                  <Text style={styles.itemSubtotal}>L. {parseFloat(item.subtotal || 0).toFixed(2)}</Text>
                </View>
                {index < (venta.detalles || venta.items || []).length - 1 && <Divider style={styles.dividerLight} />}
              </View>
            );
          })}

          <Divider style={styles.totalDivider} />

          {/* Desglose: subtotal y descuento global solo si hay descuento aplicado */}
          {parseFloat(venta.descuento_monto || 0) > 0 || parseFloat(venta.subtotal || 0) !== parseFloat(venta.total || 0) ? (
            <>
              <View style={styles.totalRow}>
                <Text style={styles.subTotalLabel}>Subtotal:</Text>
                <Text style={styles.subTotalAmount}>L. {parseFloat(venta.subtotal || 0).toFixed(2)}</Text>
              </View>
              {parseFloat(venta.descuento_monto || 0) > 0 ? (
                <View style={styles.totalRow}>
                  <Text style={[styles.subTotalLabel, { color: '#dc3545' }]}>
                    Descuento{venta.descuento_tipo === 'porcentaje' ? ` (${parseFloat(venta.descuento_valor || 0)}%)` : ''}:
                  </Text>
                  <Text style={[styles.subTotalAmount, { color: '#dc3545' }]}>
                    − L. {parseFloat(venta.descuento_monto || 0).toFixed(2)}
                  </Text>
                </View>
              ) : null}
              <Divider style={styles.dividerLight} />
            </>
          ) : null}

          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Total:</Text>
            <Text style={styles.totalAmount}>L. {parseFloat(venta.total || 0).toFixed(2)}</Text>
          </View>
        </Card.Content>
      </Card>

      {/* Acciones por estado */}
      {renderAcciones()}

      {/* Acciones generales */}
      <View style={styles.actionsContainer}>
        <Button
          mode="contained"
          icon="share"
          onPress={() => {
            Alert.alert('Formato', 'Seleccione el formato', [
              { text: 'Factura', onPress: () => generateAndSharePDF('factura') },
              { text: 'Ticket', onPress: () => generateAndSharePDF('ticket') },
              { text: 'Cancelar', style: 'cancel' },
            ]);
          }}
          style={styles.generalBtn}
          loading={generatingPDF}
          disabled={generatingPDF}
        >
          {generatingPDF ? 'Generando...' : 'Compartir'}
        </Button>

        {venta.estado === 'completada' && (
          <Button
            mode="outlined"
            icon="keyboard-return"
            onPress={() => navigation.navigate('NuevaDevolucion', { venta })}
            style={styles.generalBtn}
            textColor="#FD7E14"
          >
            Devolución
          </Button>
        )}
      </View>

      {/* Modal de entrega */}
      <Modal visible={showEntregaModal} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Confirmar Entrega</Text>
            <Text style={styles.modalSubtitle}>Pedido #{venta?.id}</Text>

            <Text style={styles.modalLabel}>Método de Pago:</Text>
            <RadioButton.Group onValueChange={setMetodoPagoEntrega} value={metodoPagoEntrega}>
              <RadioButton.Item label="Efectivo" value="efectivo" style={styles.radioItem} labelStyle={styles.radioLabel} position="leading" />
              <RadioButton.Item label="Transferencia" value="transferencia" style={styles.radioItem} labelStyle={styles.radioLabel} position="leading" />
              <RadioButton.Item label="Tarjeta" value="tarjeta" style={styles.radioItem} labelStyle={styles.radioLabel} position="leading" />
              <RadioButton.Item label="Crédito" value="credito" style={styles.radioItem} labelStyle={styles.radioLabel} position="leading" />
            </RadioButton.Group>

            <Text style={styles.modalLabel}>Notas:</Text>
            <RNTextInput
              style={styles.modalInput}
              placeholder="Notas de entrega..."
              value={notasTransicion}
              onChangeText={setNotasTransicion}
              multiline
            />

            <View style={styles.modalActions}>
              <Button mode="outlined" onPress={() => setShowEntregaModal(false)} style={styles.modalBtn}>
                Cancelar
              </Button>
              <Button mode="contained" onPress={confirmarEntrega} style={[styles.modalBtn, { backgroundColor: '#28A745' }]}>
                Confirmar Entrega
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5', padding: 8 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 10, color: '#666' },
  errorContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 16 },
  errorText: { color: '#dc3545', marginBottom: 16 },
  card: { marginBottom: 8, elevation: 2 },
  estadoChip: { marginRight: 16 },
  estadoChipText: { fontSize: 12, fontWeight: 'bold' },
  infoRow: { flexDirection: 'row', marginBottom: 12, alignItems: 'flex-start' },
  infoIconContainer: { flexDirection: 'row', alignItems: 'center', width: 130 },
  infoLabel: { fontWeight: 'bold', color: '#666', marginLeft: 6 },
  infoValue: { flex: 1, color: '#333', fontSize: 14 },
  notasContainer: { flexDirection: 'row', marginTop: 8, padding: 8, backgroundColor: '#f9f9f9', borderRadius: 4 },
  notasText: { color: '#333', flex: 1 },
  // Timeline
  timelineContainer: { paddingLeft: 4 },
  timelineRow: { flexDirection: 'row', minHeight: 50 },
  timelineLeft: { alignItems: 'center', width: 36 },
  timelineDot: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', zIndex: 1 },
  timelineDotCurrent: { width: 32, height: 32, borderRadius: 16, borderWidth: 3, borderColor: '#fff', elevation: 3 },
  timelineLine: { flex: 1, width: 3, marginVertical: -2 },
  timelineContent: { flex: 1, paddingLeft: 12, paddingBottom: 16 },
  timelineContentCurrent: { backgroundColor: '#f0f8ff', borderRadius: 8, padding: 8, marginLeft: 8, marginBottom: 8 },
  timelineLabel: { fontSize: 14, color: '#999' },
  timelineDetail: { fontSize: 11, color: '#888', marginTop: 2 },
  // Productos
  itemRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 8 },
  itemInfo: { flex: 1 },
  itemName: { fontSize: 14, fontWeight: 'bold', color: '#333' },
  itemPrice: { fontSize: 12, color: '#666', marginTop: 2 },
  itemSubtotal: { fontSize: 14, fontWeight: 'bold', color: '#28a745' },
  itemDescuento: { fontSize: 12, color: '#dc3545', marginTop: 2 },
  subTotalLabel: { fontSize: 14, color: '#555' },
  subTotalAmount: { fontSize: 14, color: '#333' },
  dividerLight: { backgroundColor: '#eee' },
  totalDivider: { marginVertical: 16, backgroundColor: '#ddd', height: 1 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  totalLabel: { fontSize: 16, fontWeight: 'bold', color: '#333' },
  totalAmount: { fontSize: 18, fontWeight: 'bold', color: '#28a745' },
  // Acciones
  actionBtn: { marginBottom: 8 },
  actionsContainer: { flexDirection: 'row', justifyContent: 'space-between', marginVertical: 16, flexWrap: 'wrap' },
  generalBtn: { flex: 1, marginHorizontal: 4 },
  // Modal
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', padding: 20 },
  modalContent: { backgroundColor: '#fff', borderRadius: 12, padding: 20 },
  modalTitle: { fontSize: 20, fontWeight: 'bold', textAlign: 'center', marginBottom: 4 },
  modalSubtitle: { fontSize: 14, color: '#666', textAlign: 'center', marginBottom: 16 },
  modalLabel: { fontSize: 14, fontWeight: 'bold', color: '#333', marginTop: 8, marginBottom: 4 },
  radioItem: { paddingVertical: 2, paddingHorizontal: 0 },
  radioLabel: { fontSize: 15, color: '#333', textAlign: 'left' },
  modalInput: { borderWidth: 1, borderColor: '#ddd', borderRadius: 8, padding: 10, minHeight: 60, textAlignVertical: 'top', marginBottom: 16 },
  modalActions: { flexDirection: 'row', justifyContent: 'space-between' },
  modalBtn: { flex: 1, marginHorizontal: 4 },
});

export default DetalleVentaScreen;
